import React, { useMemo, useState } from "react";
import { Button } from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { createContactUs } from "../../services/landing.service";

type ContactFormValues = {
  name: string;
  email?: string;
  phone?: string;
  subject?: string;
  message: string;
};

const ContactSection = () => {
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().min(2, "Name must be at least 2 characters").required("Name is required"),
        email: Yup.string()
          .trim()
          .email("Invalid email format")
          .required("Email is required")
          .transform((value) => (value === "" ? undefined : value)),
        phone: Yup.string()
          .trim()
          .optional()
           .transform((value) => {
             if (!value) {
               return undefined;
             }
             const normalizedValue = value.replace(/\D/g, "");
             return normalizedValue === "" ? undefined : normalizedValue;
           })
           .matches(/^[0-9]{10,15}$/, "Phone number must be 10 to 15 digits"),
        subject: Yup.string().trim().optional().transform((value) => (value === "" ? undefined : value)),
        message: Yup.string().min(5, "Message must be at least 5 characters").required("Message is required"),
      }),
    []
  );


  const initialValues: ContactFormValues = {
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  };

  return (
    <section className="contact-section bg-white py-[90px] px-[5%]" id="contact">
      <div className="contact-inner max-w-[580px] mx-auto text-center">

        <div className="section-label text-[0.78rem] font-bold uppercase tracking-[0.08em] text-blue-500 mb-3">Contact Us</div>
        <h2 className="section-title text-[clamp(1.6rem,3.5vw,2.4rem)] font-black leading-tight mb-4 text-slate-900">
          Have questions? We'd love to hear from you.
        </h2>
        <p className="section-sub text-xl text-slate-600 leading-relaxed mb-[48px] max-w-none">
          Whether you're a broker exploring options or a lender interested in partnership, we're here to help.
        </p>

        <div className="contact-card bg-slate-50 rounded-[16px] p-[48px] border border-slate-200 mt-[48px]">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting, resetForm, setStatus }) => {
              setSubmitError(null);
              setSuccess(false);
              try {
                const payload = {
                  name: values.name,
                  email: values.email?.trim() ? values.email.trim() : undefined,
                  phone: values.phone?.trim() ? values.phone.trim() : undefined,
                  subject: values.subject?.trim() ? values.subject.trim() : undefined,
                  message: values.message,
                };

                await createContactUs(payload);
                setSuccess(true);
                setStatus({ success: true });
                resetForm();
              } catch (e: any) {
                const msg =
                  e?.response?.data?.message ||
                  e?.message ||
                  "Failed to send message. Please try again.";
                setSubmitError(msg);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
              <Form>
                <div className="form-group mb-4 text-left">
                  <label htmlFor="contact-name" className="block text-[0.82rem] font-semibold text-slate-900 mb-1.5">Your Name</label>
                  <input
                    id="contact-name"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    type="text"
                    className={`w-full p-[11px_14px] border-2 rounded-lg bg-slate-50 text-sm transition-all ${
                      touched.name && errors.name ? "border-red-500" : "border-slate-200 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] focus:bg-white"
                    }`}
                    placeholder="Jane Smith"
                  />
                  {touched.name && errors.name && (
                    <div className="mt-1 text-xs text-red-600 font-semibold">{errors.name}</div>
                  )}
                </div>

                <div className="form-group mb-4 text-left">
                  <label htmlFor="contact-email" className="block text-[0.82rem] font-semibold text-slate-900 mb-1.5">Email Address</label>
                  <input
                    id="contact-email"
                    name="email"
                    value={values.email || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    type="email"
                    className={`w-full p-[11px_14px] border-2 rounded-lg bg-slate-50 text-sm transition-all ${
                      touched.email && errors.email ? "border-red-500" : "border-slate-200 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] focus:bg-white"
                    }`}
                    placeholder="jane@yourbrokerage.com"
                  />
                  {touched.email && errors.email && (
                    <div className="mt-1 text-xs text-red-600 font-semibold">{errors.email}</div>
                  )}
                </div>

                <div className="form-group mb-4 text-left">
                  <label htmlFor="contact-phone" className="block text-[0.82rem] font-semibold text-slate-900 mb-1.5">Phone Number</label>
                  <input
                    id="contact-phone"
                    name="phone"
                    value={values.phone || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    type="text"
                    className={`w-full p-[11px_14px] border-2 rounded-lg bg-slate-50 text-sm transition-all ${
                      touched.phone && errors.phone ? "border-red-500" : "border-slate-200 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] focus:bg-white"
                    }`}
                    placeholder="(optional)"
                  />
                  {touched.phone && errors.phone && (
                    <div className="mt-1 text-xs text-red-600 font-semibold">{errors.phone}</div>
                  )}
                </div>

                <div className="form-group mb-4 text-left">
                  <label htmlFor="contact-subject" className="block text-[0.82rem] font-semibold text-slate-900 mb-1.5">Subject</label>
                  <input
                    id="contact-subject"
                    name="subject"
                    value={values.subject || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    type="text"
                    className="w-full p-[11px_14px] border-2 border-slate-200 rounded-lg bg-slate-50 text-sm focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] focus:bg-white transition-all"
                    placeholder="I have a question about..."
                  />
                </div>

                <div className="form-group mb-4 text-left">
                  <label htmlFor="contact-message" className="block text-[0.82rem] font-semibold text-slate-900 mb-1.5">Message</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={values.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full p-[11px_14px] border-2 rounded-lg bg-slate-50 text-sm transition-all min-h-[120px] resize-vertical ${
                      touched.message && errors.message ? "border-red-500" : "border-slate-200 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] focus:bg-white"
                    }`}
                    placeholder="Tell us what's on your mind..."
                  />
                  {touched.message && errors.message && (
                    <div className="mt-1 text-xs text-red-600 font-semibold">{errors.message}</div>
                  )}
                </div>



                <Button

                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={isSubmitting}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    fontWeight: 600,
                    fontSize: "1rem",
                    mt: 1,
                  }}
                >
                  {isSubmitting ? "Sending..." : "Send Message →"}
                </Button>

                {submitError && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3.5 text-sm font-semibold text-red-800 text-center">
                    {submitError}
                  </div>
                )}

                {success && (
                  <div className="success-msg mt-4 bg-green-50 border border-green-200 rounded-lg p-3.5 text-sm font-semibold text-green-800 text-center">
                    ✓ Message sent! We'll get back to you within 24 hours.
                  </div>
                )}
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;

